import {
	DESKTOP_FONT_SIZE,
	LINE_HEIGHT,
	MOBILE_FONT_SIZE,
	PDF_FONT_SIZE,
	SPACING_FACTOR,
	THEME_TYPE,
} from '@v4/constants/pages'
import { alpha, createTheme } from '@mui/material/styles'
import { dark, light } from '@mui/material/styles/createPalette'
import { defineTheme, isDarkColor } from '@v4/utils/index'
import { PALETTE_TEXT_VARIANTS } from '@v4/constants/theme'
import { APP_MODE } from '@v4/constants/general'
import { checkIsBuilderMode } from '@v4/utils/general'
import { FONT_SIZES } from '@v4/hooks/useEditor/useEditor.const'
import { notifyError } from '@v4/utils/bugsnag'
import { deepmerge } from '@mui/utils'
import { accordionSummaryClasses } from '@mui/material/AccordionSummary'

dark.text.primary = '#FFFFFF'
dark.text.secondary = '#FFFFFF'
light.text.primary = '#323A3F'
light.text.secondary = '#323A3F'
light.text.hint = alpha('#323A3F', 0.38)
light.text.disabled = alpha('#323A3F', 0.38)

export const getFontSize = (value, unit) => {
	switch (unit) {
		case 'rem': {
			return `${Math.round((value / 16) * 1000) / 1000}rem`
		}
		default:
			return `${value}px`
	}
}

export const createTypography = (typography, fontSizes, baseFontSizes) => {
	return Object.entries(typography).reduce((acc, [key, value]) => {
		if (fontSizes[key] && baseFontSizes[key]) {
			if (key === 'fontSize') {
				acc[key] = fontSizes[key]
			} else {
				const prevFontSize = +value.fontSize.replace('rem', '')
				const fontSize = `${Math.round(((prevFontSize * fontSizes[key]) / baseFontSizes[key]) * 1000) /
					1000}rem`
				acc[key] = { ...value, fontSize }
			}
		} else {
			acc[key] = value
		}
		return acc
	}, {})
}

export const createTypographyWithMeta = (theme, metaMap, mode, isPdf, typography = {}) => {
	const isBuilderMode = checkIsBuilderMode(mode)
	const headingVariants = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2']
	const {
		headingTextFont = 'Open Sans',
		bodyTextFont = 'Open Sans',
		headingFontSize = DESKTOP_FONT_SIZE.fontSize,
		fontSize = DESKTOP_FONT_SIZE.fontSize,
	} = metaMap

	const typographyHeadingFont = (!isPdf && typography.h1?.fontFamily) || headingTextFont
	const typographyBodyFont = (!isPdf && typography.body?.fontFamily) || bodyTextFont

	function fontSizeToRem(elFontSize, htmlFontSize, baseFontSize) {
		return (
			elFontSize &&
			`${Math.round((((elFontSize / htmlFontSize) * baseFontSize) / DESKTOP_FONT_SIZE.fontSize) * 1000) /
				1000}rem`
		)
	}

	const getFontFamily = font => {
		return isPdf ? `"${font}", Emoji` : `"${font}"`
	}

	const getGradient = (name, fontSizes) => {
		switch (name) {
			case 'body1':
				return fontSizes.body1 / fontSizes.body2
			case 'subtitle1':
				return fontSizes.subtitle1 / fontSizes.body2
			case 'subtitle2':
				return fontSizes.subtitle2 / fontSizes.body2
			case 'button':
				return fontSizes.button / fontSizes.body2
			default:
				return 1
		}
	}

	const getTypographyKey = name => {
		switch (name) {
			case 'body1':
			case 'body2':
			case 'subtitle1':
			case 'subtitle2':
			case 'button':
				return 'body'
			default:
				return name
		}
	}

	const getFontWeight = (name, weight) => {
		if (!weight) {
			return undefined
		}
		const weights = {
			bold: {
				h4: 500,
				h5: 500,
				h6: 500,
				body: 500,
			},
		}

		return _.get(weights, [weight, name], weight)
	}

	return [...headingVariants, 'body1', 'body2', 'caption', 'button', 'overline'].reduce(
		(acc, variant) => {
			const baseFontSize = headingVariants.includes(variant) ? headingFontSize : fontSize
			const typographyKey = getTypographyKey(variant)
			const typographyValue = typography[typographyKey] || {}
			const { fontSize: typographyFontSize, fontFamily: typographyFontFamily } = typographyValue
			const fontFamily = getFontFamily(
				headingVariants.includes(variant) ? typographyHeadingFont : typographyBodyFont,
			)

			acc[variant] = {
				fontFamily,

				lineHeight: LINE_HEIGHT[variant],
				fontWeight: 400,
				...(isPdf
					? {
							fontWeight: headingVariants.includes(variant) ? 700 : 400,
							fontSize: fontSizeToRem(PDF_FONT_SIZE[variant], PDF_FONT_SIZE.fontSize, baseFontSize),
					  }
					: {
							textTransform: 'none',
							fontStyle: 'normal',
							textDecoration: 'none',
							letterSpacing: 'normal',
							...typographyValue,
							fontWeight: getFontWeight(typographyKey, typographyValue.fontWeight) || 400,
							fontFamily: typographyFontFamily ? getFontFamily(typographyFontFamily) : fontFamily,
							fontSize: fontSizeToRem(
								typographyFontSize
									? Math.round(
											((typographyFontSize * MOBILE_FONT_SIZE[variant]) /
												DESKTOP_FONT_SIZE[variant]) *
												getGradient(variant, MOBILE_FONT_SIZE),
									  )
									: MOBILE_FONT_SIZE[variant],
								MOBILE_FONT_SIZE.fontSize,
								MOBILE_FONT_SIZE.fontSize,
							),
							[theme.breakpoints.up('sm')]: {
								fontSize: fontSizeToRem(
									typographyFontSize
										? Math.round(typographyFontSize * getGradient(variant, DESKTOP_FONT_SIZE))
										: DESKTOP_FONT_SIZE[variant],
									DESKTOP_FONT_SIZE.fontSize,
									DESKTOP_FONT_SIZE.fontSize,
								),
							},
					  }),
				transition: isBuilderMode ? theme.transitions.create(['font-size']) : undefined,
			}
			return acc
		},
		{
			...theme.typography,
			fontFamily: getFontFamily(typographyBodyFont),
		},
	)
}

const breakpointValues = {
	xs: 0,
	sm: 600,
	md: 980,
	lg: 1164,
	xl: 1460,
}

const breakpointValuesPDF = {
	xs: 0,
	sm: 600,
}

const createThemeComponents = ({ mode, isPdf, directionality, theme }) => {
	const isBuilderMode = checkIsBuilderMode(mode)

	return {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					'&.embedded': {
						minHeight: 500,
					},
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					flexGrow: 1,
					transition: theme.transitions.create(['background-color']),
					textTransform: 'none',
					fontStyle: 'normal',
					textDecoration: 'none',
					letterSpacing: 'normal',
					'& .draft-message': {
						flexShrink: 0,
					},
					'&.draft-mode': {
						display: 'flex',
						flexDirection: 'column',
						[`& main`]: {
							minHeight: 'calc(100% - 100px)',
						},
					},
					...(isPdf
						? {
								backgroundColor: 'transparent',
								fontSize: theme.typography.body2.fontSize,
						  }
						: {
								overflowX: 'hidden',
								fontSize: theme.typography.body2.fontSize,
						  }),
				},
				main: {
					minHeight: '100%',
					display: 'flex',
					flexDirection: 'column',
					flexGrow: 1,
					flexShrink: 0,
					position: 'relative',
					...(isPdf
						? {
								justifyContent: 'flex-start',
								'@media screen': {
									maxWidth: breakpointValuesPDF.sm,
									width: '100%',
									marginLeft: 'auto',
									marginRight: 'auto',
									padding:
										{
											[APP_MODE.builder]: '0 0 10px',
											[APP_MODE.scorecard]: '10px 0',
										}[mode] || 0,
								},
						  }
						: {}),
				},
				'h1,h2,h3,h4,h5,h6,p': {
					margin: 0,
				},
				html: {
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					direction: directionality === 'rtl' ? directionality : null,
					'&.html': {
						height: '-webkit-fill-available',
					},
					WebkitFontSmoothing: 'auto',
					transition: isBuilderMode ? theme.transitions.create(['font-size']) : undefined,

					fontSize: MOBILE_FONT_SIZE.fontSize,
					...(isPdf
						? {
								[theme.breakpoints.down('sm')]: {
									fontSize: '2.667vw',
								},
								'@media print': {
									fontSize: '2.667vw',
								},
						  }
						: {
								[theme.breakpoints.down('sm')]: {
									..._.reduce(
										FONT_SIZES,
										(acc, fontSize) => {
											const fontSizeRem = `${getFontSize(fontSize, 'rem')} !important`
											acc[`& span[data-mfs="${fontSize}"]`] = {
												fontSize: fontSizeRem,
												'& *:not[data-mfs]': {
													fontSize: fontSizeRem,
												},
											}
											return acc
										},
										{},
									),
								},
						  }),
				},
			},
		},
		MuiInputBase: {
			root: {
				fontSize: theme.spacing(1.6),
				textTransform: 'none',
				fontWeight: 400,
				fontStyle: 'normal',
				textDecoration: 'none',
				letterSpacing: 'normal',
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					fontSize: theme.spacing(1.6),
					fontWeight: 400,
					textTransform: 'none',
					fontStyle: 'normal',
					textDecoration: 'none',
					letterSpacing: 'normal',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: 'unset',
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: { fontSize: theme.spacing(1.6) },
			},
		},
		MuiButton: {
			defaultProps: {
				color: 'primary',
				disableElevation: true,
				disableRipple: true,
				variant: 'contained',
			},
			styleOverrides: {
				root: isPdf
					? {
							textTransform: 'inherit',
							padding: theme.spacing(0.5, 2),
							minWidth: theme.spacing(3),
							lineHeight: 1.5,
					  }
					: {
							textTransform: 'inherit',
							padding: theme.spacing(1.5, 4),
							lineHeight: 1.5,
					  },
				text: {
					padding: theme.spacing(0.75, 1),
				},
			},
		},
		MuiAlert: {
			defaultProps: {
				variant: 'filled',
			},
			styleOverrides: {
				message: {
					fontWeight: 400,
				},
			},
		},
		MuiCheckbox: {
			defaultProps: {
				disableRipple: true,
				color: 'primary',
				size: 'small',
			},
			styleOverrides: {
				colorPrimary: {
					color: theme.palette.primary.main,
				},
			},
		},
		MuiTypography: {
			defaultProps: {
				variant: 'body2',
				variantMapping: {
					body1: 'div',
					body2: 'div',
					subtitle2: 'blockquote',
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small',
			},
		},
		MuiContainer: {
			defaultProps: {
				maxWidth: isPdf ? 'sm' : 'lg',
			},
			styleOverrides: isPdf
				? {
						root: {
							'@media print': {
								maxWidth: '100% !important',
							},
							paddingLeft: theme.spacing(theme.margins.left),
							paddingRight: theme.spacing(theme.margins.right),
							[theme.breakpoints.up('sm')]: {
								paddingLeft: theme.spacing(theme.margins.left),
								paddingRight: theme.spacing(theme.margins.right),
							},
							transition: theme.transitions.create(['padding-left', 'padding-right']),
						},
				  }
				: undefined,
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					[`&.${accordionSummaryClasses.expanded}`]: {
						minHeight: theme.spacing(4.5),
					},
				},
				expandIconWrapper: {
					marginLeft: theme.spacing(1.5),
				},
			},
		},
		MuiLink: {
			defaultProps: {
				underline: 'hover',
			},
		},
	}
}

function calcContrastColor(foreground, background) {
	// eslint-disable-next-line no-param-reassign
	background = background || this?.background?.default

	if (!foreground || !background) {
		return foreground
	}

	try {
		const darkForeground = isDarkColor(foreground)
		const darkBackground = isDarkColor(background)

		if (darkForeground !== darkBackground) {
			return foreground
		}

		return darkBackground ? dark.text.primary : light.text.primary
	} catch (e) {
		return foreground
	}
}

const createPaletteColor = (colors, contrast, contrastThreshold, textColor) =>
	Object.entries(colors).reduce((acc, [key, value]) => {
		if (!value || value === 'inherit' || value === 'transparent') return acc
		const color = calcContrastColor(value, contrast, contrastThreshold, textColor)

		if (!color) return acc

		const result = acc || {}

		result[key] = value
		return result
	}, undefined)

const createTextColors = (textPrimary, textSecondary, backgroundColor, fallbackThemeType) => {
	const type = defineTheme(backgroundColor || '#ffffff', fallbackThemeType)

	const textColors = type === THEME_TYPE.LIGHT ? { ...light.text } : { ...dark.text }
	if (textPrimary && !['inherit', 'initial'].includes(textPrimary)) {
		const primary = calcContrastColor(textPrimary, backgroundColor)
		if (primary) {
			textColors.primary = primary
		}
	}
	if (textSecondary && !['inherit', 'initial'].includes(textSecondary)) {
		const secondary = calcContrastColor(textSecondary, backgroundColor) || '#ffffff'
		if (secondary) {
			textColors.secondary = secondary
		}
	}

	return textColors
}

const createThemePalette = (settings = {}, fallback = {}, other = {}) => {
	const { palette } = settings
	const { brand_colour_primary: primary, brand_colour_secondary: secondary } = fallback.settings || {}

	const { backgroundDefault, backgroundPaper, textPrimary, textSecondary } = palette || {}

	const type = defineTheme(backgroundDefault || '#ffffff', other.type)

	return Object.entries({
		common: {
			...palette,
			cardBorder: '#D8E0E8',
			placeholder: '#939399',
			borderLight: '#D8DBE4',
			borderDark: '#505070',
			imageBorder: '#DEDEDF',
			imageIcon: '#546880',
		},
		primary: createPaletteColor({
			main: palette.primary && !['inherit', 'initial'].includes(palette.primary) ? palette.primary : primary,
		}),
		secondary: createPaletteColor({
			main:
				palette.secondary && !['inherit', 'initial'].includes(palette.secondary)
					? palette.secondary
					: secondary,
		}),
		background: createPaletteColor({
			default: backgroundDefault,
			paper: backgroundPaper,
		}),
		[PALETTE_TEXT_VARIANTS.TEXT]: createTextColors(textPrimary, textSecondary, backgroundDefault, other.type),
		[PALETTE_TEXT_VARIANTS.PAPER_TEXT]: createTextColors(textPrimary, textSecondary, backgroundPaper, other.type),
		calcContrastColor,
		getTextColors(usePaperColors = false) {
			return usePaperColors ? this[PALETTE_TEXT_VARIANTS.PAPER_TEXT] : this[PALETTE_TEXT_VARIANTS.TEXT]
		},
		...other,
		type,
		textShadow: '0 0 4px rgb(0 0 0 / 40%)',
		whiteText:
			typeof settings.backgroundImage === 'undefined'
				? other.whiteText
				: !!String(settings.backgroundImage || ''),
		scoreapp: {
			background: '#ffffff',
			text: '#546880',
			border: '#E3E9EE',
			primary: '#1A78FE',
			secondary: '#db2828',
			paper: '#FAFBFC',
			dark: '#0D276B',
			placeholder: '#748493',
			message: '#FCB92C',
			alternate: '#84D1BD',
		},
	}).reduce((acc, [key, value]) => {
		if (typeof value !== 'undefined') {
			acc[key] = value
		}
		return acc
	}, {})
}

const createThemeMargins = (theme, settings) => {
	const { marginTop = 50, marginBottom = 50, marginLeft = 50, marginRight = 50 } =
		settings.marginSize === 'custom' ? settings : {}

	return {
		top: marginTop / 10,
		bottom: marginBottom / 10,
		right: marginRight / 10,
		left: marginLeft / 10,
	}
}

const getPageTheme = ({ settings = {}, fallback = {}, mode, isPdf, directionality, typography = {} }) => {
	const palette = createThemePalette(settings, fallback)

	const theme = createTheme({
		isPdf,
		showBorders: !!Number(settings.showBorders),
		spacing: factor => (typeof factor === 'number' ? `${SPACING_FACTOR * factor}rem` : factor),
		breakpoints: { values: isPdf ? breakpointValuesPDF : breakpointValues },
		direction: directionality,
		typography: {
			h1: {
				fontSize: 54,
			},
		},
		palette,
		shape: {
			borderRadius: isPdf ? '0.2rem' : 5,
		},
	})

	if (isPdf) {
		theme.margins = createThemeMargins(theme, settings)
	}

	theme.typography = createTypographyWithMeta(theme, settings, mode, isPdf, typography)

	theme.components = createThemeComponents({
		mode,
		isPdf,
		directionality,
		theme,
	})

	// theme.overrides = createThemeOverrides({ theme, mode, isPdf, directionality })
	theme.shadows = [
		...theme.shadows,
		[
			'rgba(0,0,0,0.2) 0px -6px 6px -3px',
			'rgba(0, 0, 0, 0.14) 0px -10px 14px 1px',
			'rgba(0, 0, 0, 0.12) 0px -4px 18px 3px',
		].join(','),
	]

	return theme
}

const getSectionTheme = (initialTheme, settings, themeType) => {
	const getColor = (color, fallback) => (color && color !== 'inherit' && color !== 'transparent' ? color : fallback)
	const {
		palette: {
			background: initialBackground,
			common: initialCommon,
			secondary: initialSecondary,
			primary: initialPrimary,
			augmentColor,
		},
	} = initialTheme
	let initialBackgroundDefault
	let initialBackgroundPaper

	try {
		initialBackgroundDefault = themeType === THEME_TYPE.DARK ? initialBackground.paper : initialBackground.default
		initialBackgroundPaper = themeType === THEME_TYPE.DARK ? initialBackground.default : initialBackground.paper
	} catch (error) {
		notifyError(error, {
			metaData: {
				info: {
					initialTheme,
					settings,
					themeType,
					initialData: window.initialData,
				},
			},
		})
	}

	const { backgroundDefault, backgroundPaper, primary, secondary, textPrimary, textSecondary, ...restColors } =
		settings.palette || {}
	const sectionBackgroundDefault = getColor(backgroundDefault, initialBackgroundDefault)

	const palette = createThemePalette(
		{
			...settings,
			palette: {
				...initialCommon,
				primary: getColor(primary, initialPrimary.main),
				secondary: getColor(secondary, initialSecondary.main),
				textPrimary: getColor(textPrimary, initialCommon.textPrimary),
				textSecondary: getColor(textSecondary, initialCommon.textSecondary),
				backgroundDefault: sectionBackgroundDefault,
				backgroundPaper:
					backgroundPaper === 'transparent'
						? sectionBackgroundDefault
						: getColor(backgroundPaper, initialBackgroundPaper),
				...restColors,
			},
		},
		{},
		{ type: themeType, whiteText: initialTheme.palette.whiteText },
	)

	palette.secondary = augmentColor({ color: { main: palette.secondary?.main || initialSecondary.main } })
	palette.primary = augmentColor({ color: { main: palette.primary?.main || initialPrimary.main } })

	const newTheme = deepmerge(initialTheme, {
		showBorders:
			typeof settings.showBorders !== 'undefined' ? !!Number(settings.showBorders) : initialTheme.showBorders,
		palette,
	})

	return createTheme(newTheme)
}

const updatePaletteTextTheme = (parentTheme, textValue) => {
	const textValues = Object.entries(textValue).reduce((acc, [key, value]) => {
		if (value) acc[key] = value
		return acc
	}, {})

	return createTheme(
		deepmerge(parentTheme, {
			palette: {
				...parentTheme.palette,
				text: {
					...parentTheme.palette.text,
					...textValues,
				},
			},
		}),
	)
}

const calcMargin = ({ spacing, value, minMargin, isMobile, negative }) => {
	const result = Math.max(Number(value || 0) / (isMobile ? 18 : 10), minMargin ? minMargin + 0.125 : 0)

	return result > 0 ? spacing(negative ? -result : result) : 0
}

const getWhiteTheme = theme => {
	if (!theme || !theme.palette) return theme

	const newTheme = getSectionTheme(theme, {
		palette: {
			primary: theme.palette.common.white,
			secondary: theme.palette.common.white,
		},
	})

	return updatePaletteTextTheme(newTheme, {
		primary: theme.palette.common.white,
		secondary: theme.palette.common.white,
	})
}

const getDirectionValue = (direction, map = null, defaultValue = 1) => {
	if (!direction) return null
	return map?.[direction] ?? defaultValue
}

const getMobileSpacingTop = ({ disabled, metaData, minMargins, theme, negative }) => {
	if (disabled) {
		return null
	}
	const marginMeta = metaData.overrideXsMargins?.toBoolean() ? metaData.xsMarginTop : metaData.marginTop

	return calcMargin({
		spacing: theme.spacing,
		value: marginMeta?.metaValue,
		minMargin: minMargins.top,
		isMobile: true,
		negative,
	})
}

const getMobileSpacingBottom = ({ metaData, minMargins, theme, disabled, negative }) => {
	if (disabled) {
		return null
	}

	const marginMeta = metaData.overrideXsMargins?.toBoolean() ? metaData.xsMarginBottom : metaData.marginBottom

	return calcMargin({
		spacing: theme.spacing,
		value: marginMeta?.metaValue,
		minMargin: minMargins.bottom,
		isMobile: true,
		negative,
	})
}

export {
	createThemePalette,
	breakpointValues,
	getPageTheme,
	getSectionTheme,
	updatePaletteTextTheme,
	calcMargin,
	calcContrastColor,
	getWhiteTheme,
	getDirectionValue,
	getMobileSpacingTop,
	getMobileSpacingBottom,
}
